// Organizasyon teklifi formu — yeni / düzenle / kopyala
//   /events/new            → yeni teklif ("Onay Bekliyor", Ayarlar'daki aktif sezon)
//   /events/edit/:id       → düzenleme (durum, sezon, PDF dosya adı, "kapora alındı" sunucuda KORUNUR)
//   /events/new?copy=<id>  → kopya: müşteri, etkinlik ve kalemler taşınır; id/durum/sezon/pdfFile/kapora alındı,
//                            etkinlik tarihi, kapora tarihleri ve geçerlilik tarihi taşınmaz
//
// Hesap sırası (utils/eventPricing.js computeEventTotals): ara toplam → indirim → KDV → genel toplam → kapora/kalan.
// KDV oranı KALEM BAŞINA seçilir (yemek %10, salon/hizmet %20); "dahil / hariç" teklif başına.
// Sunucu aynı hesabı yapar ve gönderilen toplamlarla karşılaştırır (fark → 400).
// Sayı alanları HAM metindir ("1.500", "12,5"); kayıtta sayıya çevrilir. Number('1.500') KULLANILMAZ (= 1,5).
// Kayıttan sonra teklif sunucudan yeniden okunur → PDF üretilir → yüklenir. PDF hatası kaydı geri ALMAZ.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import {
  ArrowLeft, Save, AlertTriangle, Tag, Receipt, Wallet, CalendarDays, Building2, FileText, Info, Lock, Copy,
} from 'lucide-react';
import { EventPDF } from './EventPDF';
import EventItemsEditor from './EventItemsEditor';
import {
  fetchEventOffer, createEventOffer, updateEventOffer, uploadEventPdf, fetchEventSettings,
  DEFAULT_EVENT_SETTINGS, itemToPayload, itemFromSaved, emptyItem, validateItems,
} from './eventApi';
import {
  computeEventTotals, toEventNumber, addDaysLocal, todayLocal, visibleVatGroups, vatRatesText, itemVatRate,
  VAT_MODES, DEFAULT_VAT_RATE, VAT_RATE_OPTIONS, DEPOSIT_TYPES, EVENT_TYPE_SUGGESTIONS,
} from '../../utils/eventPricing';
import {
  parseDiscountInput, discountUnitMismatch, discountLabel, formatMoneyTR, round2,
  DEFAULT_DISCOUNT_NAME, DISCOUNT_TYPES,
} from '../../utils/pricing';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useSeason } from '../../context/SeasonContext';
import { isValidSeason, offerSeason, ALL_SEASONS } from '../../utils/season';

const SERVER_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
const LOGO_KEYS = ['light', 'dark', 'favicon', 'pdfBackground', 'loginImage'];

const CURRENCIES = [
  ['TL', 'Türk Lirası (TL)'],
  ['USD', 'Amerikan Doları (USD)'],
  ['EUR', 'Euro (EUR)'],
  ['GBP', 'İngiliz Sterlini (GBP)'],
];
const CURRENCY_CODES = CURRENCIES.map(([code]) => code);

// İndirim adı önerileri (serbest metin de yazılabilir) — event_offers.discountName VARCHAR(100)
const DISCOUNT_NAME_SUGGESTIONS = [DEFAULT_DISCOUNT_NAME, 'Özel İndirim', 'Kurumsal İndirim', 'Grup İndirimi', 'Sezon Dışı İndirimi'];
const DISCOUNT_NAME_MAX = 100;
const VALIDITY_SHORTCUTS = [7, 15, 30];
// Sunucu sınırları (backend/server.js EVENT_MAX_QUANTITY)
const MAX_QUANTITY = 100000;

const STATUS_STYLES = {
  'Onay Bekliyor': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  'Onaylandı': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  'Onaylanmadı': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const inputCls = 'w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 disabled:opacity-60';
const labelCls = 'block text-xs text-gray-500 dark:text-gray-300 mb-1';
const warnCls = 'text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1';
const fmt = formatMoneyTR;
const fmtPct = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

// Sayı → giriş kutusu metni (Türkçe, binlik ayraçsız): 20 → "20", 18.5 → "18,5"
const toInputText = (n) => {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('tr-TR', { maximumFractionDigits: 2, useGrouping: false }) : '';
};
// Ayarlar'daki varsayılan KDV oranı (yeni satır ve oranı olmayan eski kalem için); geçersizse %20
const settingsVatRate = (s) => {
  const r = toEventNumber(s?.defaultVatRate);
  return Number.isFinite(r) && r >= 0 && r <= 100 ? r : DEFAULT_VAT_RATE;
};

const formatDateLong = (dateStr) => {
  const [y, m, d] = String(dateStr || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};
const formatDateShort = (dateStr) => {
  const [y, m, d] = String(dateStr || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
// İki 'YYYY-MM-DD' arasındaki gün farkı (yerel tarih)
const daysBetween = (from, to) => {
  const [y1, m1, d1] = String(from).split('-').map(Number);
  const [y2, m2, d2] = String(to).split('-').map(Number);
  if (!y1 || !y2) return NaN;
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
};
// "20:00" – "02:00" → 360 dk (gece yarısını geçen etkinlik)
const durationMinutes = (start, end) => {
  const toMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + m; };
  if (!/^\d{2}:\d{2}/.test(start || '') || !/^\d{2}:\d{2}/.test(end || '')) return 0;
  return (toMin(end) - toMin(start) + 1440) % 1440;
};

const isPersonUnit = (it) => String(it?.unit || '').trim().toLocaleLowerCase('tr') === 'kişi';

// Form satırı → kayıt biçimi. Sunucu adet ve birim fiyatı kuruşa yuvarlayıp toplamı yuvarlanmış değerlerle
// hesaplar; aynısı burada yapılmazsa "2,555" gibi girişte toplamlar uyuşmaz ve kayıt reddedilir.
const normalizeItem = (it) => {
  const p = itemToPayload(it);
  return {
    ...p,
    quantity: Number.isFinite(p.quantity) ? round2(p.quantity) : p.quantity,
    unitPrice: Number.isFinite(p.unitPrice) ? round2(p.unitPrice) : p.unitPrice,
    vatRate: Number.isFinite(p.vatRate) ? round2(p.vatRate) : p.vatRate,
  };
};

const defaultValidUntil = (s) => {
  const raw = s?.defaultValidityDays;
  const n = Number(raw);
  if (raw === '' || raw === null || raw === undefined || !Number.isFinite(n) || n < 0) return '';
  return addDaysLocal(todayLocal(), Math.round(n));
};

// Boş form (yeni teklif) — varsayılanlar Ayarlar > Organizasyon'dan
const blankForm = (s) => ({
  customerName: '', companyName: '', phone: '', email: '', taxOffice: '', taxNumber: '', address: '',
  sourceId: '', currency: 'TL',
  eventType: '', eventName: '', eventDate: '', startTime: '', endTime: '', guestCount: '', venue: '',
  discountEnabled: false, discountName: DEFAULT_DISCOUNT_NAME, discountType: 'percentage', discountInput: '',
  vatMode: VAT_MODES.includes(s?.defaultVatMode) ? s.defaultVatMode : 'included',
  depositEnabled: false, depositType: 'percentage', depositInput: toInputText(s?.defaultDepositPercent),
  depositDueDate: '', balanceDueDate: '',
  validUntil: defaultValidUntil(s), notes: '', internalNote: '',
});

// Kayıtlı teklif (sunucu GET biçimi: tutarlar number, tarihler 'YYYY-MM-DD') → form durumu
const formFromOffer = (o, isCopy, s) => {
  const base = blankForm(s);
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  const discountValue = Number(o.discountValue);
  const hasDiscount = DISCOUNT_TYPES.includes(o.discountType) && discountValue > 0;
  const depositValue = Number(o.depositValue);
  const hasDeposit = DEPOSIT_TYPES.includes(o.depositType) && depositValue > 0;
  const adjustText = (type, value) => (type === 'percentage' ? toInputText(value) : fmt(value));
  return {
    ...base,
    customerName: str(o.customerName),
    companyName: str(o.companyName),
    phone: str(o.phone),
    email: str(o.email),
    taxOffice: str(o.taxOffice),
    taxNumber: str(o.taxNumber),
    address: str(o.address),
    sourceId: o.source?.id !== undefined && o.source?.id !== null ? String(o.source.id) : '',
    currency: CURRENCY_CODES.includes(o.currency) ? o.currency : 'TL',
    eventType: str(o.eventType),
    eventName: str(o.eventName),
    eventDate: isCopy ? '' : str(o.eventDate).slice(0, 10),
    startTime: str(o.startTime).slice(0, 5),
    endTime: str(o.endTime).slice(0, 5),
    guestCount: o.guestCount === null || o.guestCount === undefined ? '' : String(o.guestCount),
    venue: str(o.venue),
    ...(hasDiscount ? {
      discountEnabled: true,
      discountName: o.discountName || DEFAULT_DISCOUNT_NAME,
      discountType: o.discountType,
      discountInput: adjustText(o.discountType, discountValue),
    } : {}),
    vatMode: VAT_MODES.includes(o.vatMode) ? o.vatMode : 'included',
    ...(hasDeposit ? {
      depositEnabled: true,
      depositType: o.depositType,
      depositInput: adjustText(o.depositType, depositValue),
    } : {}),
    // Kapora tarihleri etkinlik tarihine bağlıdır → kopyada taşınmaz
    depositDueDate: isCopy ? '' : str(o.depositDueDate).slice(0, 10),
    balanceDueDate: isCopy ? '' : str(o.balanceDueDate).slice(0, 10),
    validUntil: isCopy ? base.validUntil : str(o.validUntil).slice(0, 10),
    notes: str(o.notes),
    internalNote: str(o.internalNote),
  };
};

const getJson = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

// Kart — bileşen dışında tanımlı (içeride tanımlansa her render'da yeniden kurulur, odak kaybolurdu)
function Section({ icon: Icon, title, aside, children }) {
  return (
    <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700">
      {(title || aside) && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          {title && (
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
              {Icon && <Icon size={18} className="text-gray-500 dark:text-gray-400" />}
              {title}
            </h3>
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v}
          className={`flex-1 px-3 py-2 text-sm font-medium text-center transition-colors ${value === v
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
          {text}
        </button>
      ))}
    </div>
  );
}

export default function EventForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyId = id ? '' : (searchParams.get('copy') || '').trim();
  const isEdit = !!id;
  const isCopy = !isEdit && !!copyId;

  const { currentUser } = useAuth();
  const token = currentUser?.token;
  const canWrite = !!currentUser?.permissions?.write;
  const { incrementReads, incrementWrites } = useData();
  const { activeSeason, selectedSeason, setSelectedSeason } = useSeason();

  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // '' | 'saving' | 'pdf' — kayıt sürerken form ve düğme kilitli (mükerrer kayıt olmasın)
  const [savingStage, setSavingStage] = useState('');
  const savingRef = useRef(false);
  const [dirty, setDirty] = useState(false);

  const [offerSources, setOfferSources] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsWarning, setSettingsWarning] = useState(false);
  const [eventSettings, setEventSettings] = useState(DEFAULT_EVENT_SETTINGS);

  const [form, setForm] = useState(() => blankForm(DEFAULT_EVENT_SETTINGS));
  const [items, setItems] = useState(() => [emptyItem()]);
  // Düzenlenen teklifin formda değişmeyen bilgileri: { season, status, depositPaid, depositAmount, eventDate, validUntil }
  const [saved, setSaved] = useState(null);
  const [copiedFrom, setCopiedFrom] = useState('');

  const saving = savingStage !== '';

  useEffect(() => {
    let cancelled = false;
    const sourceOfferId = id || copyId;
    const copying = !id && !!copyId;

    const load = async () => {
      setPageLoading(true);
      setLoadError('');
      setSaved(null);
      setCopiedFrom('');
      const [sourcesData, logosData, bankData, evResult, offerResult] = await Promise.all([
        getJson(`${API_URL}/offerSources`),
        getJson(`${API_URL}/settings/logos`),
        getJson(`${API_URL}/settings/bank`),
        // Koşullar metni + hazır kalemler. Alınamazsa form AÇILMAZ: PDF'e otelin koşulları yerine örnek koşullar basılmasın
        fetchEventSettings().then(s => ({ s }), err => ({ error: err?.message || 'Organizasyon ayarları alınamadı.' })),
        sourceOfferId
          ? fetchEventOffer(token, sourceOfferId).then(offer => ({ offer }), err => ({ error: err?.message || 'Teklif alınamadı.' }))
          : Promise.resolve(null),
      ]);
      if (cancelled) return;
      incrementReads(sourceOfferId ? 5 : 4);
      if (evResult.error) {
        setLoadError(`${evResult.error} Teklif PDF'ine yanlış koşullar basılmaması için form açılmadı; sayfayı yenileyip tekrar deneyin.`);
        setPageLoading(false);
        return;
      }
      const evSettings = evResult.s;

      // Resim URL'lerini düzelt (PDF oluşturucu için tam URL gerekli)
      const logos = logosData && typeof logosData === 'object' ? { ...logosData } : null;
      if (logos) {
        LOGO_KEYS.forEach(key => {
          if (typeof logos[key] === 'string' && logos[key].startsWith('/uploads')) logos[key] = SERVER_URL + logos[key];
        });
      }
      const bank = bankData && typeof bankData === 'object' ? bankData : null;
      setOfferSources(Array.isArray(sourcesData) ? sourcesData : []);
      setSettings({ ...(logos || {}), bank: bank || {} });
      setSettingsWarning(!logos || !bank);
      setEventSettings(evSettings);

      if (offerResult?.error) {
        setLoadError(offerResult.error);
        setPageLoading(false);
        return;
      }
      const offer = offerResult?.offer;
      if (offer) {
        setForm(formFromOffer(offer, copying, evSettings));
        // Oranı olmayan (eski) kalem teklifin kayıtlı oranını alır
        const offerRate = offer.vatRate !== null && Number.isFinite(Number(offer.vatRate)) ? Number(offer.vatRate) : settingsVatRate(evSettings);
        setItems(Array.isArray(offer.items) && offer.items.length > 0
          ? offer.items.map(it => itemFromSaved(it, offerRate))
          : [emptyItem(settingsVatRate(evSettings))]);
        if (copying) {
          setCopiedFrom([offer.customerName, offer.companyName, offer.eventType].filter(Boolean).join(' · '));
        } else {
          setSaved({
            season: offerSeason(offer),
            status: offer.status || '',
            depositPaid: !!offer.depositPaid,
            depositAmount: Number(offer.depositAmount) || 0,
            eventDate: String(offer.eventDate || '').slice(0, 10),
            validUntil: String(offer.validUntil || '').slice(0, 10),
          });
        }
      } else {
        setForm(blankForm(evSettings));
        setItems([emptyItem(settingsVatRate(evSettings))]);
      }
      setDirty(false);
      setPageLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [id, copyId, token, incrementReads]);

  // Kaydedilmemiş değişiklik varken sekme kapatılır/yenilenirse tarayıcı uyarsın
  useEffect(() => {
    if (!dirty || saving) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, saving]);

  const setField = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
  };
  const bind = (field) => ({ value: form[field], onChange: e => setField(field, e.target.value) });
  const handleItemsChange = (next) => {
    setItems(next);
    setDirty(true);
  };

  const handleBack = () => {
    if (dirty && canWrite && !saving && !window.confirm('Kaydedilmemiş değişiklikler var. Çıkmak istediğinize emin misiniz?')) return;
    navigate('/events');
  };

  // Kapora türü değişince değer anlamını yitirir (%30 ≠ 30 TL) → yüzdede varsayılan oran, tutarda boş
  const handleDepositTypeChange = (type) => {
    if (type === form.depositType) return;
    setForm(f => ({ ...f, depositType: type, depositInput: type === 'percentage' ? toInputText(eventSettings.defaultDepositPercent) : '' }));
    setDirty(true);
  };

  // ── Hesaplar (her render'da; saf fonksiyonlar) ───────────────────────────────────────────────
  const cur = form.currency;
  const payloadItems = items.map(normalizeItem);
  const subTotal = computeEventTotals({ items: payloadItems }).subTotal;

  // İndirim: { value, error }. Hata varken indirim uygulanmaz (OfferForm ile aynı kurallar).
  const discount = (() => {
    if (!form.discountEnabled) return { value: 0, error: '' };
    const raw = form.discountInput;
    const value = round2(parseDiscountInput(raw, form.discountType));
    let error = '';
    if (!form.discountName.trim()) error = 'İndirim adını girin.';
    else if (String(raw).trim() === '') error = 'İndirim değerini girin.';
    else if (discountUnitMismatch(raw, form.discountType)) error = form.discountType === 'amount'
      ? '"Tutar" seçili ama değerde % var. Yüzde indirim için "Yüzde (%)"yi seçin.'
      : '"Yüzde" seçili ama değerde TL var. Tutar indirimi için "Tutar"ı seçin.';
    else if (!Number.isFinite(value) || value <= 0) error = `Geçersiz değer: "${raw}". Ör. ${form.discountType === 'percentage' ? '10 ya da 12,5' : '5000 ya da 5.000'}`;
    else if (form.discountType === 'percentage' && value > 100) error = "Yüzde 100'den büyük olamaz.";
    else if (form.discountType === 'amount' && value > subTotal) error = `İndirim tutarı ara toplamdan (${fmt(subTotal)} ${cur}) büyük olamaz.`;
    return error ? { value: 0, error } : { value, error: '' };
  })();
  const discountOn = form.discountEnabled && !discount.error;

  // KDV oranı kalem başına; teklif düzeyindeki oran yalnızca oranı olmayan kalemin varsayılanı
  const defaultVatRate = settingsVatRate(eventSettings);
  const applyVatRateToAll = (rate) => handleItemsChange(items.map(it => ({ ...it, vatRate: String(rate) })));

  const totalsArgs = {
    items: payloadItems,
    discountType: discountOn ? form.discountType : null,
    discountValue: discountOn ? discount.value : 0,
    vatMode: form.vatMode,
    vatRate: defaultVatRate,
  };
  const beforeDeposit = computeEventTotals(totalsArgs);

  // Kapora: genel toplam (KDV dahil ödenecek tutar) üzerinden
  const deposit = (() => {
    if (!form.depositEnabled) return { value: 0, error: '' };
    const raw = form.depositInput;
    const value = round2(parseDiscountInput(raw, form.depositType));
    let error = '';
    if (String(raw).trim() === '') error = 'Kapora değerini girin.';
    else if (discountUnitMismatch(raw, form.depositType)) error = form.depositType === 'amount'
      ? '"Tutar" seçili ama değerde % var. Yüzde için "Yüzde (%)"yi seçin.'
      : '"Yüzde" seçili ama değerde TL var. Tutar için "Tutar"ı seçin.';
    else if (!Number.isFinite(value) || value <= 0) error = `Geçersiz değer: "${raw}". Ör. ${form.depositType === 'percentage' ? '30 ya da 25,5' : '10000 ya da 10.000'}`;
    else if (form.depositType === 'percentage' && value > 100) error = "Yüzde 100'den büyük olamaz.";
    else if (form.depositType === 'amount' && value > beforeDeposit.total) error = `Kapora tutarı genel toplamdan (${fmt(beforeDeposit.total)} ${cur}) büyük olamaz.`;
    return error ? { value: 0, error } : { value, error: '' };
  })();
  const depositOn = form.depositEnabled && !deposit.error;

  const totals = depositOn
    ? computeEventTotals({ ...totalsArgs, depositType: form.depositType, depositValue: deposit.value })
    : beforeDeposit;

  // Kişi sayısı (tam sayı; "1.200" Türkçe biçimde 1200 okunur)
  const guest = (() => {
    const raw = String(form.guestCount ?? '').trim();
    if (!raw) return { value: null, error: '' };
    const n = toEventNumber(raw);
    if (!Number.isInteger(n) || n < 0 || n > MAX_QUANTITY) return { value: null, error: 'Kişi sayısı tam sayı olmalı (ör. 150).' };
    return { value: n, error: '' };
  })();
  const personItems = items.filter(isPersonUnit);
  const personItemsOutOfSync = guest.value > 0 && personItems.some(it => toEventNumber(it.quantity) !== guest.value);
  const applyGuestCount = () => {
    if (!(guest.value > 0)) return;
    handleItemsChange(items.map(it => (isPersonUnit(it) ? { ...it, quantity: String(guest.value) } : it)));
  };

  // Tarih uyarıları (engellemez; kayıtta toplu onay sorulur)
  const today = todayLocal();
  const eventDateChanged = !isEdit || form.eventDate !== saved?.eventDate;
  const validUntilChanged = !isEdit || form.validUntil !== saved?.validUntil;
  const eventPast = !!form.eventDate && form.eventDate < today;
  const validPast = !!form.validUntil && form.validUntil < today;
  const validAfterEvent = !!form.validUntil && !!form.eventDate && form.validUntil > form.eventDate;
  const overnight = !!form.startTime && !!form.endTime && form.endTime < form.startTime;
  const durationMin = durationMinutes(form.startTime, form.endTime);
  const depositAfterEvent = form.depositEnabled && !!form.depositDueDate && !!form.eventDate && form.depositDueDate > form.eventDate;
  const balanceBeforeDeposit = form.depositEnabled && !!form.depositDueDate && !!form.balanceDueDate && form.balanceDueDate < form.depositDueDate;
  const balanceAfterEvent = form.depositEnabled && !!form.balanceDueDate && !!form.eventDate && form.balanceDueDate > form.eventDate;
  const taxNumberOdd = form.taxNumber.trim() !== '' && !/^\d{10,11}$/.test(form.taxNumber.trim());

  // ── Kaydet ────────────────────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWrite || savingRef.current) return;

    // 1) Kesin hatalar (onay sorularından ÖNCE — onaylar verildikten sonra iptal olmasın)
    if (!form.customerName.trim()) return alert('Yetkili adı soyadını girin.');
    const itemsError = validateItems(items);
    if (itemsError) return alert(`Kalemler: ${itemsError}`);
    const tinyIndex = payloadItems.findIndex(p => !(p.quantity > 0));
    if (tinyIndex !== -1) return alert(`${tinyIndex + 1}. kalemin adedi çok küçük (en az 0,01).`);
    const hugeIndex = payloadItems.findIndex(p => p.quantity > MAX_QUANTITY);
    if (hugeIndex !== -1) return alert(`${hugeIndex + 1}. kalemin adedi en fazla ${MAX_QUANTITY.toLocaleString('tr-TR')} olabilir.`);
    if (guest.error) return alert(`Kişi sayısı: ${guest.error}`);
    if (form.discountEnabled && discount.error) return alert(`İndirim: ${discount.error}\n\nİndirim uygulanmayacaksa "İndirim uygula" kutusunun işaretini kaldırın.`);
    if (form.depositEnabled && deposit.error) return alert(`Kapora: ${deposit.error}\n\nKapora istenmeyecekse "Kapora iste" kutusunun işaretini kaldırın.`);

    // 2) Uyarılar — tek onayda toplanır
    const warnings = [];
    if (discountOn && subTotal > 0 && totals.discountAmount / subTotal >= 0.5) {
      warnings.push(`İndirim ara toplamın %${Math.round((totals.discountAmount / subTotal) * 100)}'i: -${fmt(totals.discountAmount)} ${cur}. Genel toplam ${fmt(totals.total)} ${cur} olacak.`);
    }
    if (subTotal === 0) warnings.push(`Kalemlerin toplamı 0 ${cur}.`);
    if (!form.phone.trim() && !form.email.trim()) warnings.push('Telefon ve e-posta boş: teklif WhatsApp/e-posta ile paylaşılamaz.');
    if (eventPast && eventDateChanged) warnings.push(`Etkinlik tarihi (${formatDateShort(form.eventDate)}) geçmişte.`);
    if (validPast && validUntilChanged) warnings.push(`Teklif geçerlilik tarihi (${formatDateShort(form.validUntil)}) geçmişte.`);
    if (depositAfterEvent) warnings.push('Kapora son ödeme tarihi etkinlik tarihinden sonra.');
    if (balanceBeforeDeposit) warnings.push('Kalan tutarın son ödeme tarihi kapora tarihinden önce.');
    if (balanceAfterEvent) warnings.push('Kalan tutarın son ödeme tarihi etkinlik tarihinden sonra.');
    if (isEdit && saved?.depositPaid && (!depositOn || Math.abs(totals.depositAmount - saved.depositAmount) > 0.009)) {
      warnings.push(`Bu teklifte kapora ALINDI olarak işaretli; kapora ${fmt(saved.depositAmount)} → ${depositOn ? fmt(totals.depositAmount) : 'yok'} ${cur} olarak değişiyor.`);
    }
    if (isEdit && saved?.status === 'Onaylandı') warnings.push('Teklif ONAYLANMIŞ: PDF yeniden oluşturulacak, müşteriye gönderilen link yeni hâli gösterecek.');
    if (settingsWarning) warnings.push('Logo/banka ayarları yüklenemedi: PDF antetsiz ya da banka bilgisiz oluşabilir.');
    if (warnings.length > 0 && !window.confirm(`Lütfen kontrol edin:\n\n• ${warnings.join('\n• ')}\n\nYine de kaydedilsin mi?`)) return;

    const sourceRow = offerSources.find(s => String(s.id) === String(form.sourceId));
    if (form.sourceId && !sourceRow) return alert('Seçilen teklif kaynağı bulunamadı. Lütfen yeniden seçin.');

    const nz = (v) => { const t = String(v ?? '').trim(); return t || null; };
    const payload = {
      customerName: form.customerName.trim(),
      companyName: nz(form.companyName),
      phone: nz(form.phone),
      email: nz(form.email),
      taxOffice: nz(form.taxOffice),
      taxNumber: nz(form.taxNumber),
      address: nz(form.address),
      source: sourceRow ? { id: Number(sourceRow.id), name: sourceRow.name } : null,
      currency: cur,
      eventType: nz(form.eventType),
      eventName: nz(form.eventName),
      eventDate: form.eventDate || null,
      startTime: form.startTime ? form.startTime.slice(0, 5) : null,
      endTime: form.endTime ? form.endTime.slice(0, 5) : null,
      guestCount: guest.value,
      venue: nz(form.venue),
      items: payloadItems,
      discountName: discountOn ? form.discountName.trim() : null,
      discountType: discountOn ? form.discountType : null,
      discountValue: discountOn ? discount.value : null,
      vatMode: form.vatMode,
      // Tek oranlı teklifte o oran, karma oranlıda varsayılan (her kalem kendi vatRate'ini taşır)
      vatRate: totals.vatRate === null ? defaultVatRate : totals.vatRate,
      depositType: depositOn ? form.depositType : null,
      depositValue: depositOn ? deposit.value : null,
      depositDueDate: depositOn ? (form.depositDueDate || null) : null,
      balanceDueDate: depositOn ? (form.balanceDueDate || null) : null,
      validUntil: form.validUntil || null,
      notes: nz(form.notes),
      internalNote: nz(form.internalNote),
      // Ekranda görünen toplamlar — sunucu kendi hesabıyla karşılaştırır (PDF'teki tutar = kaydedilen tutar)
      subTotal: totals.subTotal,
      discountAmount: totals.discountAmount,
      totalPrice: totals.total,
      depositAmount: totals.depositAmount,
      vatAmount: totals.vatAmount,
      // Durum ve sezon yalnızca YENİ teklifte yazılır; düzenlemede sunucu zaten değiştirmez
      ...(isEdit ? {} : { status: 'Onay Bekliyor', ...(isValidSeason(activeSeason) ? { season: activeSeason } : {}) }),
    };

    savingRef.current = true;
    setSavingStage('saving');
    let savedId = id;
    try {
      if (isEdit) {
        await updateEventOffer(token, id, payload);
      } else {
        const created = await createEventOffer(token, payload);
        savedId = created?.id;
        if (!savedId) throw new Error('Sunucu teklif numarası döndürmedi.');
      }
      incrementWrites(1);
    } catch (err) {
      console.error('Organizasyon teklifi kaydedilemedi:', err);
      savingRef.current = false;
      setSavingStage('');
      alert(`Teklif kaydedilemedi: ${err.message}`);
      return;
    }

    // PDF — sunucudaki tam hâlden (yuvarlanmış tutarlar, createdAt, pdfFile) üretilir.
    // Hata olursa teklif yine de kaydedilmiştir: formda kalınmaz (tekrar "Kaydet" mükerrer kayıt açardı).
    setSavingStage('pdf');
    let fresh = null;
    let pdfError = '';
    try {
      fresh = await fetchEventOffer(token, savedId);
      incrementReads(1);
      const blob = await pdf(<EventPDF offer={fresh} settings={settings} eventSettings={eventSettings} />).toBlob();
      await uploadEventPdf(token, savedId, blob);
      incrementWrites(1);
    } catch (err) {
      console.error('Organizasyon PDF hatası:', err);
      pdfError = err?.message || String(err);
    }

    // Kaydedilen teklif başka sezondaysa görünüm o sezona geçsin; yoksa listede hiç görünmez
    const savedSeason = isEdit ? saved?.season : (fresh ? offerSeason(fresh) : activeSeason);
    if (isValidSeason(savedSeason) && selectedSeason !== ALL_SEASONS && selectedSeason !== savedSeason) {
      setSelectedSeason(savedSeason);
    }
    setDirty(false);
    if (pdfError) {
      alert(`Teklif kaydedildi ancak PDF oluşturulamadı: ${pdfError}\n\nListeden "PDF'i yeniden oluştur" ile tekrar deneyebilirsiniz.`);
    }
    navigate('/events');
  };

  // Metin kutusunda Enter formu göndermesin (yarım teklif kaydedilip PDF'i üretilmesin)
  const preventImplicitSubmit = (e) => {
    if (e.key === 'Enter' && e.target?.tagName === 'INPUT') e.preventDefault();
  };

  // ── Görünüm ───────────────────────────────────────────────────────────────────────────────────
  const pageTitle = isEdit ? 'Organizasyon Teklifini Düzenle' : isCopy ? 'Organizasyon Teklifi (Kopya)' : 'Yeni Organizasyon Teklifi';
  const seasonBadge = isEdit ? saved?.season : activeSeason;

  const header = (
    <div className="flex flex-wrap items-center gap-3 mb-6">
      <button type="button" onClick={handleBack} aria-label="Organizasyon tekliflerine dön"
        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
        <ArrowLeft size={24} />
      </button>
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{pageTitle}</h1>
      {/* Sezon bilgi amaçlı: yeni teklif Ayarlar'daki aktif sezona, düzenlenen teklif kendi sezonunda kalır */}
      {!pageLoading && seasonBadge && (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
          {seasonBadge} Sezonu
        </span>
      )}
      {isEdit && saved?.status && (
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[saved.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
          {saved.status}
        </span>
      )}
      {isEdit && saved?.depositPaid && (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
          Kapora alındı
        </span>
      )}
    </div>
  );

  if (pageLoading) {
    return (
      <div className="max-w-6xl mx-auto pb-10">
        {header}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700 text-gray-500 dark:text-gray-400">
          Yükleniyor…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-6xl mx-auto pb-10">
        {header}
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-6 rounded-lg text-red-700 dark:text-red-300">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle size={18} /> {isCopy ? 'Kopyalanacak teklif yüklenemedi.' : 'Teklif yüklenemedi.'}</p>
          <p className="mt-1 text-sm">{loadError}</p>
          <button type="button" onClick={() => navigate('/events')} className="mt-4 px-4 py-2 rounded bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
            Listeye dön
          </button>
        </div>
      </div>
    );
  }

  const discountValueEmpty = String(form.discountInput).trim() === '';
  const depositValueEmpty = String(form.depositInput).trim() === '';
  const validDays = form.validUntil ? daysBetween(today, form.validUntil) : NaN;
  const perPerson = guest.value > 0 && totals.total > 0 ? round2(totals.total / guest.value) : 0;
  const vatGroups = visibleVatGroups(totals);
  // Kalemlerde seçili oran (fiyat henüz girilmemiş olsa da): "KDV uygulanmıyor" yalnızca hepsi %0 ise
  const anyVatRate = payloadItems.some(it => itemVatRate(it, defaultVatRate) > 0);

  return (
    <div className="max-w-6xl mx-auto pb-10">
      {header}

      {!canWrite && (
        <div className="mb-6 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
          <Lock size={16} className="shrink-0 mt-0.5" />
          <span>Salt okunur: organizasyon teklifi oluşturma/düzenleme (yazma) yetkiniz yok. Değişiklik için yöneticinize başvurun.</span>
        </div>
      )}
      {isCopy && (
        <div className="mb-6 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm flex items-start gap-2">
          <Copy size={16} className="shrink-0 mt-0.5" />
          <span>
            {copiedFrom ? <><strong>{copiedFrom}</strong> teklifinden kopyalandı. </> : 'Kopya teklif. '}
            Müşteri, etkinlik ve kalem bilgileri aktarıldı; etkinlik tarihi ve kapora tarihleri boş, geçerlilik tarihi yenilendi.
            Kaydedince <strong>yeni</strong> bir teklif oluşur (Onay Bekliyor{isValidSeason(activeSeason) ? `, ${activeSeason} sezonu` : ''}).
          </span>
        </div>
      )}
      {settingsWarning && (
        <div className="mb-6 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>Logo / banka ayarları yüklenemedi. PDF antetsiz ya da banka bilgisiz oluşabilir; sayfayı yenileyip tekrar deneyin.</span>
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={preventImplicitSubmit} className="space-y-6">
        <fieldset disabled={!canWrite || saving} className="min-w-0 space-y-6">

          {/* 1) Müşteri */}
          <Section icon={Building2} title="Müşteri ve Firma Bilgileri">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="ev-customerName" className={labelCls}>Yetkili Adı Soyadı *</label>
                <input id="ev-customerName" type="text" required maxLength={255} className={inputCls} {...bind('customerName')} placeholder="ör. Ayşe Yılmaz" />
              </div>
              <div>
                <label htmlFor="ev-companyName" className={labelCls}>Firma Adı</label>
                <input id="ev-companyName" type="text" maxLength={255} className={inputCls} {...bind('companyName')} placeholder="Bireysel müşteride boş bırakın" />
              </div>
              <div>
                <label htmlFor="ev-phone" className={labelCls}>Telefon</label>
                <input id="ev-phone" type="tel" maxLength={50} className={inputCls} {...bind('phone')} placeholder="ör. 0532 123 45 67" />
              </div>
              <div>
                <label htmlFor="ev-email" className={labelCls}>E-posta</label>
                <input id="ev-email" type="email" maxLength={255} className={inputCls} {...bind('email')} placeholder="ornek@firma.com" />
              </div>
              <div>
                <label htmlFor="ev-taxOffice" className={labelCls}>Vergi Dairesi</label>
                <input id="ev-taxOffice" type="text" maxLength={100} className={inputCls} {...bind('taxOffice')} />
              </div>
              <div>
                <label htmlFor="ev-taxNumber" className={labelCls}>Vergi No / T.C. Kimlik No</label>
                <input id="ev-taxNumber" type="text" inputMode="numeric" maxLength={20} autoComplete="off" className={inputCls} {...bind('taxNumber')} />
                {taxNumberOdd && (
                  <p className={`mt-1 ${warnCls}`}><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Vergi no 10 haneli, T.C. kimlik no 11 haneli olur. Kontrol edin.</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label htmlFor="ev-address" className={labelCls}>Adres (fatura adresi)</label>
                <textarea id="ev-address" rows={2} maxLength={2000} className={inputCls} {...bind('address')} />
              </div>
              <div>
                <label htmlFor="ev-source" className={labelCls}>Teklif Kaynağı (isteğe bağlı)</label>
                <select id="ev-source" className={inputCls} {...bind('sourceId')}>
                  <option value="">Seçilmedi</option>
                  {offerSources.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="ev-currency" className={labelCls}>Para Birimi</label>
                <select id="ev-currency" className={inputCls} {...bind('currency')}>
                  {CURRENCIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
              </div>
            </div>
          </Section>

          {/* 2) Etkinlik */}
          <Section icon={CalendarDays} title="Etkinlik Bilgileri">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="ev-eventType" className={labelCls}>Organizasyon Türü</label>
                <input id="ev-eventType" type="text" list="event-type-options" maxLength={100} className={inputCls} {...bind('eventType')} placeholder="ör. Nişan" />
                <datalist id="event-type-options">
                  {EVENT_TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label htmlFor="ev-eventName" className={labelCls}>Etkinlik Adı (PDF'te başlık olarak görünür)</label>
                <input id="ev-eventName" type="text" maxLength={255} className={inputCls} {...bind('eventName')} placeholder="ör. Ayşe & Mehmet Nişan Töreni" />
              </div>
              <div>
                <label htmlFor="ev-eventDate" className={labelCls}>Tarih</label>
                <input id="ev-eventDate" type="date" min="2000-01-01" max="2100-12-31" className={inputCls} {...bind('eventDate')} />
                {form.eventDate && (
                  <p className={`mt-1 text-xs ${eventPast ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    {formatDateLong(form.eventDate)}{eventPast ? ' — geçmiş tarih' : ''}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ev-startTime" className={labelCls}>Başlangıç Saati</label>
                <input id="ev-startTime" type="time" className={inputCls} {...bind('startTime')} />
              </div>
              <div>
                <label htmlFor="ev-endTime" className={labelCls}>Bitiş Saati</label>
                <input id="ev-endTime" type="time" className={inputCls} {...bind('endTime')} />
                {durationMin > 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Süre: {Math.floor(durationMin / 60)} saat{durationMin % 60 ? ` ${durationMin % 60} dk` : ''}{overnight ? ' (ertesi gün biter)' : ''}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="ev-guestCount" className={labelCls}>Kişi Sayısı</label>
                <input id="ev-guestCount" type="text" inputMode="numeric" autoComplete="off" maxLength={7}
                  className={`${inputCls} ${guest.error ? 'border-red-400 dark:border-red-500' : ''}`} {...bind('guestCount')} placeholder="ör. 150" />
                {guest.error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{guest.error}</p>}
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <label htmlFor="ev-venue" className={labelCls}>Salon / Mekan</label>
                <input id="ev-venue" type="text" maxLength={255} className={inputCls} {...bind('venue')} placeholder="ör. Balo Salonu, Teras, Havuz Başı" />
              </div>
            </div>
            {guest.value > 0 && personItems.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {personItemsOutOfSync ? (
                  <button type="button" onClick={applyGuestCount}
                    className="px-3 py-1.5 rounded border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 font-medium">
                    Kişi sayısını 'kişi' birimli kalemlere uygula ({personItems.length} kalem → {guest.value.toLocaleString('tr-TR')} kişi)
                  </button>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">'Kişi' birimli kalemlerin adedi kişi sayısıyla aynı.</span>
                )}
              </div>
            )}
          </Section>

          {/* 3) Kalemler */}
          <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700">
            <EventItemsEditor
              items={items}
              onChange={handleItemsChange}
              currency={cur}
              library={Array.isArray(eventSettings.itemLibrary) ? eventSettings.itemLibrary : []}
              packages={Array.isArray(eventSettings.packages) ? eventSettings.packages : []}
              readOnly={!canWrite}
              title="Teklif Kalemleri"
              defaultVatRate={defaultVatRate}
            />
          </section>

          {/* 4) İndirim — teklif başına tek, kalemlerin ara toplamına uygulanır */}
          <Section>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4" checked={form.discountEnabled} onChange={e => setField('discountEnabled', e.target.checked)} />
              <Tag size={18} className="text-gray-500 dark:text-gray-400" />
              <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">İndirim uygula</span>
            </label>
            {form.discountEnabled && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="ev-discountName" className={labelCls}>İndirim Adı (PDF'te bu adla görünür)</label>
                    <input id="ev-discountName" type="text" list="ev-discount-name-options" maxLength={DISCOUNT_NAME_MAX}
                      className={inputCls} {...bind('discountName')} placeholder={DEFAULT_DISCOUNT_NAME} />
                    <datalist id="ev-discount-name-options">
                      {DISCOUNT_NAME_SUGGESTIONS.map(n => <option key={n} value={n} />)}
                    </datalist>
                  </div>
                  <div>
                    <span className={labelCls}>İndirim Türü</span>
                    <Segmented label="İndirim türü" value={form.discountType} onChange={v => setField('discountType', v)}
                      options={[['percentage', 'Yüzde (%)'], ['amount', `Tutar (${cur})`]]} />
                  </div>
                  <div>
                    <label htmlFor="ev-discountValue" className={labelCls}>
                      {form.discountType === 'percentage' ? 'İndirim Oranı (%)' : `İndirim Tutarı (${cur})`}
                    </label>
                    <div className="relative">
                      {/* Metin: "5.000" Türkçe biçimde doğru okunur (type=number 5 okurdu) */}
                      <input id="ev-discountValue" type="text" inputMode="decimal" autoComplete="off"
                        className={`${inputCls} pr-12 ${discount.error && !discountValueEmpty ? 'border-red-400 dark:border-red-500' : ''}`}
                        {...bind('discountInput')} placeholder={form.discountType === 'percentage' ? 'ör. 10' : 'ör. 5.000'} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                        {form.discountType === 'percentage' ? '%' : cur}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Değer henüz yazılmadıysa ipucu gri; geçersiz değer kırmızı; geçerliyse düşülecek tutar yeşil */}
                <p className={`mt-3 text-sm ${!discount.error ? 'text-green-700 dark:text-green-400' : discountValueEmpty ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                  {discount.error
                    ? discount.error
                    : `${discountLabel({ name: form.discountName.trim(), type: form.discountType, value: discount.value })}: -${fmt(totals.discountAmount)} ${cur}`}
                </p>
              </>
            )}
          </Section>

          {/* 5) KDV — "dahil / hariç" teklif başına; oran her kalemde ayrı (Kalemler bölümündeki "KDV" sütunu) */}
          <Section icon={Receipt} title="KDV">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <span className={labelCls}>Girilen fiyatlar</span>
                <Segmented label="KDV" value={form.vatMode} onChange={v => setField('vatMode', v)}
                  options={[['included', 'Fiyatlar KDV dahil'], ['excluded', 'KDV hariç (ayrı eklenir)']]} />
              </div>
              <div>
                <span className={labelCls}>Tüm kalemlerin oranını değiştir</span>
                <div className="flex flex-wrap gap-1">
                  {VAT_RATE_OPTIONS.map(r => (
                    <button key={r} type="button" onClick={() => applyVatRateToAll(r)}
                      className="px-2.5 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                      Hepsi %{r}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              KDV oranı her kalemde ayrı seçilir (ör. yemek %10, salon / hizmet %20). İndirim, kalemlere tutarları oranında dağıtılır.
            </p>
            {vatGroups.length > 0 ? (
              <ul className="mt-2 space-y-0.5 text-sm text-gray-600 dark:text-gray-300">
                {vatGroups.map(g => (
                  <li key={g.rate}>
                    {form.vatMode === 'excluded'
                      ? <>%{fmtPct(g.rate)} KDV — {fmt(g.base)} {cur} üzerinden: <strong>+{fmt(g.vat)} {cur}</strong></>
                      : <>%{fmtPct(g.rate)} KDV'li kalemler {fmt(g.base)} {cur} — içindeki KDV: <strong>{fmt(g.vat)} {cur}</strong></>}
                  </li>
                ))}
                {vatGroups.length > 1 && (
                  <li className="pt-0.5">
                    {form.vatMode === 'excluded' ? 'Toplam KDV' : 'Toplam içindeki KDV (bilgi amaçlı)'}: <strong>{fmt(totals.vatAmount)} {cur}</strong>
                  </li>
                )}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {anyVatRate ? 'KDV tutarı, kalemlerin fiyatı girilince burada görünür.' : 'KDV uygulanmıyor (tüm kalemler %0).'}
              </p>
            )}
          </Section>

          {/* 6) Kapora (ön ödeme) planı */}
          <Section>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="h-4 w-4" checked={form.depositEnabled} onChange={e => setField('depositEnabled', e.target.checked)} />
              <Wallet size={18} className="text-gray-500 dark:text-gray-400" />
              <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">Kapora (ön ödeme) iste</span>
            </label>
            {form.depositEnabled && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                  <div>
                    <span className={labelCls}>Kapora Türü</span>
                    <Segmented label="Kapora türü" value={form.depositType} onChange={handleDepositTypeChange}
                      options={[['percentage', 'Yüzde (%)'], ['amount', `Tutar (${cur})`]]} />
                  </div>
                  <div>
                    <label htmlFor="ev-depositValue" className={labelCls}>
                      {form.depositType === 'percentage' ? 'Kapora Oranı (% — genel toplamdan)' : `Kapora Tutarı (${cur})`}
                    </label>
                    <div className="relative">
                      <input id="ev-depositValue" type="text" inputMode="decimal" autoComplete="off"
                        className={`${inputCls} pr-12 ${deposit.error && !depositValueEmpty ? 'border-red-400 dark:border-red-500' : ''}`}
                        {...bind('depositInput')} placeholder={form.depositType === 'percentage' ? 'ör. 30' : 'ör. 10.000'} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                        {form.depositType === 'percentage' ? '%' : cur}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="ev-depositDueDate" className={labelCls}>Kapora Son Ödeme Tarihi</label>
                    <input id="ev-depositDueDate" type="date" min="2000-01-01" max="2100-12-31" className={inputCls} {...bind('depositDueDate')} />
                  </div>
                  <div>
                    <label htmlFor="ev-balanceDueDate" className={labelCls}>Kalan Tutar Son Ödeme Tarihi</label>
                    <input id="ev-balanceDueDate" type="date" min="2000-01-01" max="2100-12-31" className={inputCls} {...bind('balanceDueDate')} />
                  </div>
                </div>
                <p className={`mt-3 text-sm ${!deposit.error ? 'text-green-700 dark:text-green-400' : depositValueEmpty ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                  {deposit.error
                    ? deposit.error
                    : `Kapora: ${fmt(totals.depositAmount)} ${cur} — Kalan: ${fmt(totals.balance)} ${cur}`}
                </p>
                {(depositAfterEvent || balanceBeforeDeposit || balanceAfterEvent || (isEdit && saved?.depositPaid)) && (
                  <div className="mt-2 space-y-1">
                    {depositAfterEvent && <p className={warnCls}><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Kapora son ödeme tarihi etkinlik tarihinden sonra.</p>}
                    {balanceBeforeDeposit && <p className={warnCls}><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Kalan tutarın son ödeme tarihi kapora tarihinden önce.</p>}
                    {balanceAfterEvent && <p className={warnCls}><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Kalan tutarın son ödeme tarihi etkinlik tarihinden sonra.</p>}
                    {isEdit && saved?.depositPaid && (
                      <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-1">
                        <Info size={12} className="shrink-0 mt-0.5" /> Bu teklifte kapora alındı olarak işaretli ({fmt(saved.depositAmount)} {cur}). İşaret listeden değiştirilir.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </Section>

          {/* 7) Teklif bilgileri */}
          <Section icon={FileText} title="Teklif Bilgileri">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="ev-validUntil" className={labelCls}>Teklif Geçerlilik Tarihi</label>
                <input id="ev-validUntil" type="date" min="2000-01-01" max="2100-12-31" className={inputCls} {...bind('validUntil')} />
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {VALIDITY_SHORTCUTS.map(d => (
                    <button key={d} type="button" onClick={() => setField('validUntil', addDaysLocal(today, d))}
                      className="px-2 py-0.5 rounded text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                      +{d} gün
                    </button>
                  ))}
                </div>
                {form.validUntil && Number.isFinite(validDays) && (
                  <p className={`mt-1 text-xs ${validPast ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    {validPast ? 'Geçerlilik tarihi geçmiş.' : validDays === 0 ? 'Bugün sona eriyor.' : `${validDays} gün geçerli.`}
                    {validAfterEvent ? ' (Etkinlik tarihinden sonra.)' : ''}
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label htmlFor="ev-notes" className={labelCls}>Notlar (PDF'e basılır, müşteri görür)</label>
                <textarea id="ev-notes" rows={4} maxLength={5000} className={inputCls} {...bind('notes')}
                  placeholder="ör. Menü 2 hafta önceden kesinleştirilir. Dekorasyon talepleri ayrıca fiyatlandırılır." />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="ev-internalNote" className={`${labelCls} flex items-center gap-1`}>
                  <Lock size={12} /> İç not — yalnızca panelde görünür (PDF'e basılmaz, müşteriye gitmez)
                </label>
                <textarea id="ev-internalNote" rows={2} maxLength={5000}
                  className={`${inputCls} bg-amber-50/60 dark:bg-amber-900/20`} {...bind('internalNote')}
                  placeholder="ör. Müşteri fiyat için tekrar arayacak; masa düzeni 10'lu yuvarlak." />
              </div>
            </div>
          </Section>
        </fieldset>

        {/* Özet çubuğu */}
        <div className="bg-blue-50 dark:bg-blue-900/50 p-6 rounded-lg flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="w-full md:w-auto">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-blue-800 dark:text-blue-200">
                <span>Ara Toplam: <strong>{fmt(totals.subTotal)} {cur}</strong></span>
                {totals.discountAmount > 0 && (
                  <span className="text-green-700 dark:text-green-300">
                    {discountLabel({ name: form.discountName.trim(), type: form.discountType, value: discount.value })}: <strong>-{fmt(totals.discountAmount)} {cur}</strong>
                  </span>
                )}
                {form.vatMode === 'excluded' && vatGroups.map(g => (
                  <span key={g.rate}>KDV (%{fmtPct(g.rate)}): <strong>+{fmt(g.vat)} {cur}</strong></span>
                ))}
                {form.vatMode === 'included' && totals.vatAmount > 0 && (
                  <span className="text-blue-700/80 dark:text-blue-300/80">İçindeki KDV ({vatRatesText(vatGroups, fmtPct)}): {fmt(totals.vatAmount)} {cur}</span>
                )}
                {perPerson > 0 && (
                  <span className="text-blue-700/80 dark:text-blue-300/80">Kişi başı: {fmt(perPerson)} {cur}</span>
                )}
              </div>
              <p className="text-2xl font-bold text-blue-900 dark:text-white mt-1">
                Genel Toplam: {fmt(totals.total)} {cur}
                {anyVatRate && <span className="ml-2 text-sm font-medium text-blue-700 dark:text-blue-200">(KDV dahil)</span>}
              </p>
              {depositOn && totals.depositAmount > 0 && (
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Kapora: <strong>{fmt(totals.depositAmount)} {cur}</strong>
                  {form.depositDueDate ? ` (son ${formatDateShort(form.depositDueDate)})` : ''}
                  {' — '}Kalan: <strong>{fmt(totals.balance)} {cur}</strong>
                  {form.balanceDueDate ? ` (son ${formatDateShort(form.balanceDueDate)})` : ''}
                </p>
              )}
            </div>
          </div>
          {canWrite && (
            <button type="submit" disabled={saving}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait flex items-center gap-2 w-full md:w-auto justify-center">
              <Save size={20} />
              {savingStage === 'saving' ? 'Kaydediliyor…' : savingStage === 'pdf' ? 'PDF hazırlanıyor…' : (isEdit ? 'Güncelle' : 'Teklifi Oluştur')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
